const activeField = { name: 'isActive', label: 'Active', type: 'switch', defaultValue: true };

const provider = (row, code) => (row?.sourceConfigurations || []).find(
  item => String(item.providerCode).toLowerCase() === code.toLowerCase()
);

const metadata = configuration => {
  try { return JSON.parse(configuration?.metadataJson || '{}'); } catch { return {}; }
};

export const connectionInitial = (row, connection) => {
  const configuration = provider(row, connection.provider);
  const parsedMetadata = metadata(configuration);
  const connectionMetadata = parsedMetadata?.connection && typeof parsedMetadata.connection === 'object'
    ? parsedMetadata.connection
    : {};
  const values = { ...parsedMetadata, ...connectionMetadata };
  const initial = Object.fromEntries(
    connection.fields
      .filter(field => field.type !== 'password' && values[field.name] !== undefined)
      .map(field => [field.name, values[field.name]])
  );
  if (configuration?.externalId !== null && configuration?.externalId !== undefined && configuration?.externalId !== '') {
    initial[connection.existingId] = Number(configuration.externalId);
  } else {
    delete initial[connection.existingId];
  }
  connection.fields.filter(field => field.type === 'password').forEach(field => {
    initial[field.name] = '';
  });
  return initial;
};

const companyFields = [
  { name: 'code', label: 'Code', required: true },
  { name: 'name', label: 'Name', required: true },
  { name: 'legalName', label: 'Legal name' },
  { name: 'taxId', label: 'Tax ID' },
  activeField
];

const institutionFields = [
  { name: 'code', label: 'Code', required: true },
  { name: 'name', label: 'Name', required: true },
  {
    name: 'sourceType', label: 'Source type', type: 'creatable-select', required: true,
    options: ['BankStatements', 'CreditCardReports', 'ExpenseReports', 'Other']
  },
  { name: 'countryCode', label: 'Country code' },
  { name: 'bic', label: 'BIC' },
  { name: 'description', label: 'Description', type: 'textarea' },
  activeField
];

const accountFields = [
  { name: 'companyId', label: 'Company', type: 'company', required: true, numeric: true },
  { name: 'financialInstitutionId', label: 'Financial institution', type: 'institution', required: true, numeric: true },
  { name: 'name', label: 'Name', required: true },
  { name: 'accountNumber', label: 'Account number', required: true, inputMode: 'numeric' },
  { name: 'currency', label: 'Currency', required: true },
  { name: 'accountType', type: 'hidden', defaultValue: 'BankAccount' },
  activeField
];

const cardFields = [
  { name: 'companyId', label: 'Company', type: 'company', required: true, numeric: true },
  { name: 'financialInstitutionId', label: 'Financial institution', type: 'institution', numeric: true, optional: true },
  { name: 'cardholderName', label: 'Cardholder name', required: true },
  {
    name: 'accountType', label: 'Account type', type: 'creatable-select', required: true,
    options: ['Individual', 'Corporate']
  },
  { name: 'lastFourDigits', label: 'Last four digits', required: true, pattern: '\\d{4}', maxLength: 4, inputMode: 'numeric' },
  { name: 'paymentType', label: 'Payment type' },
  activeField
];

export const managementConfigs = {
  companies: {
    title: 'Companies', singular: 'company', id: 'companyId', service: 'companies', fields: companyFields,
    columns: [
      ['Code', 'code'], ['Name', 'name'], ['Legal name', 'legalName'], ['Tax ID', 'taxId']
    ],
    badges: ['SAP', 'BAC'],
    importHelp: 'Required: Code, Name. Optional: LegalName, TaxId, IsActive. Spanish aliases: Codigo, Nombre, RazonSocial, NIT, Activo.',
    connections: [
      {
        label: 'Configure SAP', provider: 'sap', existingId: 'existingSapCompanyId',
        fields: [
          { name: 'companyDb', label: 'Company database', newRequired: true },
          { name: 'baseUrl', label: 'Base URL', newRequired: true, type: 'url' },
          { name: 'companyName', label: 'Company name' },
          { name: 'userName', label: 'User name', newRequired: true },
          { name: 'password', label: 'Password', type: 'password', newRequired: true, editHint: 'Leave empty to keep the stored password.' },
          { name: 'taxCodeDefault', label: 'Default tax code' },
          { name: 'taxExemptCodeDefault', label: 'Default tax-exempt code' },
          { name: 'supplierGroupCodeDefault', label: 'Default supplier group', numeric: true },
          { name: 'supplierSeriesDefault', label: 'Default supplier series', numeric: true },
          { name: 'supplierAccountDefault', label: 'Default supplier account' },
          { name: 'isConsolidator', label: 'Consolidator', type: 'switch' }
        ]
      },
      {
        label: 'Configure BAC', provider: 'bac', existingId: 'existingBacCompanyId',
        fields: [{ name: 'companyName', label: 'Company name' }, { name: 'taxId', label: 'Tax ID' }]
      }
    ]
  },
  institutions: {
    title: 'Financial Institutions', singular: 'financial institution', id: 'financialInstitutionId', service: 'institutions', fields: institutionFields,
    columns: [['Code', 'code'], ['Name', 'name'], ['Source type', 'sourceType'], ['Country', 'countryCode'], ['BIC', 'bic']],
    badges: ['BAC'],
    importHelp: 'Required: Code, Name, SourceType. Optional: CountryCode, Bic, Description, IsActive.',
    connections: [{
      label: 'Configure BAC connection', provider: 'bac', existingId: 'existingBankAuthId',
      fields: [
        { name: 'bankName', label: 'Bank name' }, { name: 'countryCode', label: 'Country code' },
        { name: 'bankBic', label: 'Bank BIC' }, { name: 'authUrl', label: 'Authentication URL', type: 'url', newRequired: true },
        { name: 'refreshUrl', label: 'Refresh URL', type: 'url', newRequired: true },
        { name: 'apiKey', label: 'API key', newRequired: true },
        { name: 'apiSecret', label: 'API secret', type: 'password', newRequired: true, editHint: 'Stored secrets are never displayed; leave empty to retain the current value.' }
      ]
    }]
  },
  accounts: {
    title: 'Bank Accounts', singular: 'bank account', id: 'financialAccountId', service: 'accounts', fields: accountFields,
    columns: [['Company', 'companyName'], ['Institution', 'financialInstitutionName'], ['Name', 'name'], ['Account number', 'accountNumber'], ['Currency', 'currency']],
    filters: ['companyId', 'financialInstitutionId'],
    importHelp: 'Use CompanyId or CompanyCode and FinancialInstitutionId or FinancialInstitutionCode. Required: Name, AccountNumber, Currency. Optional: AccountType, IsActive.',
    connections: [{
      label: 'Configure BAC', provider: 'bac', existingId: 'existingCompanyBankAccountId',
      fields: [
        { name: 'bankServiceId', label: 'BAC service', type: 'bank-service', numeric: true, required: true },
        { name: 'clientBic', label: 'Client BIC', required: true },
        { name: 'accountOwnerBic', label: 'Account owner BIC', required: true },
        { name: 'defaultRptgReqdMsgNmId', label: 'Default reporting message name ID', required: true },
        { name: 'defaultRptgPeriodType', label: 'Default reporting period type', required: true },
        { name: 'defaultMsgIdPrefix', label: 'Default message ID prefix', required: true }
      ]
    }]
  },
  cards: {
    title: 'Payment Cards', singular: 'payment card', id: 'paymentCardId', service: 'cards', fields: cardFields,
    columns: [['Company', 'companyName'], ['Institution', 'financialInstitutionName'], ['Cardholder', 'cardholderName'], ['Account type', 'accountType'], ['Last four', 'lastFourDigits'], ['Payment type', 'paymentType']],
    filters: ['companyId', 'financialInstitutionId'],
    maskCard: true, importCompany: true,
    importHelp: 'Spanish headers are supported: Nombre que aparece en la tarjeta, Tipo de cuenta, Último segmento, Tipo de pago. An optional company overrides the company for every row.'
  }
};

export const hasProvider = (row, code) => Boolean(provider(row, code));
